import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { solutionClaimsService } from '../services/solutionClaimsService';
import type { SolutionClaim } from '../services/subProjectService';

interface ClaimsTabProps {
  subProjectId: string;
  solutionId: string;
  canEdit: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  product: 'Product',
  method: 'Method',
  programme: 'Programme',
  storage_medium: 'Medium',
  computer: 'Computer',
  use: 'Use',
};

const ClaimsTab: React.FC<ClaimsTabProps> = ({ subProjectId, solutionId, canEdit }) => {
  const { t } = useTranslation();
  const [claims, setClaims] = useState<SolutionClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [editingClaim, setEditingClaim] = useState<Record<string, string>>({});
  const [decomposing, setDecomposing] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const data = await solutionClaimsService.list(subProjectId, solutionId);
      setClaims(data);
    } catch {
      setError(t('claims.loadError', 'Failed to load claims'));
    } finally {
      setLoading(false);
    }
  }, [subProjectId, solutionId, t]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const data = await solutionClaimsService.generate(subProjectId, solutionId);
      setClaims(data);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('claims.generateError', 'Generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveClaimText = async (claimId: string) => {
    const newText = editingClaim[claimId];
    if (!newText) return;
    try {
      const updated = await solutionClaimsService.update(subProjectId, solutionId, claimId, { claim_text: newText });
      setClaims(prev => prev.map(c => c.id === claimId ? updated : c));
      setEditingClaim(prev => { const n = { ...prev }; delete n[claimId]; return n; });
    } catch {
      setError(t('claims.saveError', 'Failed to save claim'));
    }
  };

  const handleDecompose = async (claimId: string) => {
    setDecomposing(prev => new Set(prev).add(claimId));
    setError(null);
    try {
      const updated = await solutionClaimsService.decompose(subProjectId, solutionId, claimId);
      setClaims(prev => prev.map(c => c.id === claimId ? updated : c));
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('claims.decomposeError', 'Decomposition failed'));
    } finally {
      setDecomposing(prev => { const n = new Set(prev); n.delete(claimId); return n; });
    }
  };

  const handleDelete = async (claimId: string) => {
    try {
      await solutionClaimsService.delete(subProjectId, solutionId, claimId);
      setClaims(prev => prev.filter(c => c.id !== claimId));
    } catch {
      setError(t('claims.deleteError', 'Failed to delete claim'));
    }
  };

  const isStale = (c: SolutionClaim) =>
    c.decomposition_is_stale === true || (c.features.length > 0 && !c.decomposition_at);

  if (loading) {
    return <div style={{ padding: '8px 10px', fontSize: 12, color: '#94a3b8' }}>{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div style={{ padding: '6px 10px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', flex: 1 }}>
          {t('claims.title', 'Claims')} {claims.length > 0 && `(${claims.length})`}
        </span>
        {canEdit && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              fontSize: 11, padding: '2px 10px', borderRadius: 3,
              border: '1px solid #2563eb', background: generating ? '#e0e7ff' : '#eff6ff',
              color: '#2563eb', fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer',
            }}
          >
            {generating ? t('claims.generating', 'Generating...') : t('claims.generateAll', 'Generate all claims')}
          </button>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>
          {error}
        </div>
      )}

      {claims.length === 0 && !generating && (
        <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px 0' }}>
          {t('claims.empty', 'No claims yet. Click "Generate all claims" to draft claims from the solution description.')}
        </div>
      )}

      {claims.map(claim => {
        const isExpanded = expandedFeatures.has(claim.id);
        const editing = editingClaim[claim.id] !== undefined;
        const stale = isStale(claim);

        return (
          <div key={claim.id} style={{ border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, overflow: 'hidden' }}>
            {/* Claim header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 8px', background: claim.is_independent ? '#f8fafc' : '#fff' }}>
              <span style={{ fontSize: 10, color: '#64748b', minWidth: 18, fontWeight: 600, marginTop: 1 }}>
                {claim.claim_number}.
              </span>
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 3,
                background: '#e0e7ff', color: '#3730a3', fontWeight: 600,
                whiteSpace: 'nowrap', marginTop: 1,
              }}>
                {CATEGORY_LABELS[claim.category] ?? claim.category}
              </span>
              {!claim.is_independent && (
                <span style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                  {t('claims.dependsOn', 'dep.')} {claim.depends_on.join(', ')}
                </span>
              )}
              {stale && (
                <span style={{ fontSize: 10, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3, padding: '1px 5px', marginLeft: 2 }}>
                  {t('claims.stale', 'stale')}
                </span>
              )}
              <div style={{ flex: 1 }} />
              {canEdit && stale && (
                <button
                  onClick={() => handleDecompose(claim.id)}
                  disabled={decomposing.has(claim.id)}
                  style={{ fontSize: 10, padding: '1px 7px', borderRadius: 3, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: decomposing.has(claim.id) ? 'not-allowed' : 'pointer' }}
                >
                  {decomposing.has(claim.id) ? '...' : t('claims.reDecompose', 'Re-decompose')}
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => handleDelete(claim.id)}
                  style={{ fontSize: 10, padding: '1px 7px', borderRadius: 3, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                >
                  {t('common.delete', 'Delete')}
                </button>
              )}
            </div>

            {/* Claim text */}
            <div style={{ padding: '4px 8px 4px 32px' }}>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <textarea
                    value={editingClaim[claim.id]}
                    onChange={e => setEditingClaim(prev => ({ ...prev, [claim.id]: e.target.value }))}
                    style={{ width: '100%', fontSize: 11, fontFamily: 'inherit', border: '1px solid #94a3b8', borderRadius: 3, padding: '4px 6px', resize: 'vertical', minHeight: 60 }}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleSaveClaimText(claim.id)} style={{ fontSize: 10, padding: '2px 8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                      {t('common.save', 'Save')}
                    </button>
                    <button onClick={() => setEditingClaim(prev => { const n = { ...prev }; delete n[claim.id]; return n; })} style={{ fontSize: 10, padding: '2px 8px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}>
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{ fontSize: 11, color: '#334155', cursor: canEdit ? 'text' : 'default', lineHeight: 1.5 }}
                  onClick={() => canEdit && setEditingClaim(prev => ({ ...prev, [claim.id]: claim.claim_text }))}
                >
                  {claim.claim_text}
                </div>
              )}
            </div>

            {/* Features section */}
            {claim.features.length > 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9' }}>
                <button
                  onClick={() => setExpandedFeatures(prev => {
                    const n = new Set(prev);
                    if (n.has(claim.id)) n.delete(claim.id); else n.add(claim.id);
                    return n;
                  })}
                  style={{ width: '100%', textAlign: 'left', fontSize: 10, color: '#64748b', padding: '3px 8px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {isExpanded ? '▼' : '▶'} {t('claims.features', 'Features')} ({claim.features.length})
                </button>
                {isExpanded && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '2px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '40%' }}>{t('claims.featureText', 'Text')}</th>
                        <th style={{ padding: '2px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', width: '35%' }}>{t('claims.construedMeaning', 'Construed meaning')}</th>
                        <th style={{ padding: '2px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{t('claims.class', 'Class')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claim.features.map(f => (
                        <tr key={f.feature_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '2px 8px', verticalAlign: 'top', color: '#334155' }}>{f.text}</td>
                          <td style={{ padding: '2px 8px', verticalAlign: 'top', color: '#64748b' }}>{f.construed_meaning || '—'}</td>
                          <td style={{ padding: '2px 8px', verticalAlign: 'top', color: '#94a3b8' }}>{f.feature_class}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClaimsTab;
